import type { ReactNode } from "react";
import { KCN_VALUES } from "@/lib/values";
import { Section, SectionEyebrow } from "./Section";

export function ValuesHeading({ className }: { className?: string }) {
  return (
    <h2 className={className ?? "headline-lg"}>
      Grit. Grace. Growth.{" "}
      <span className="serif-italic text-primary">Give.</span>
    </h2>
  );
}

/**
 * Shared "Grit. Grace. Growth. Give." section.
 * Copy lives in src/lib/values.ts - edit it there, every page updates.
 *
 * variant="grid": eyebrow + heading + four cards (home page)
 * variant="compact": heading + intro + optional children (side column usage)
 */
export function ValuesSection({
  variant = "grid",
  eyebrow = "What we're built on",
  intro = "Four words that decide who thrives on this team - and who doesn't. Read them honestly. If they describe you, we should talk.",
  tone = "darker",
  children,
}: {
  variant?: "grid" | "compact";
  eyebrow?: string;
  intro?: string;
  tone?: "dark" | "darker" | "cream";
  children?: ReactNode;
}) {
  if (variant === "compact") {
    return (
      <>
        {eyebrow ? <SectionEyebrow>{eyebrow}</SectionEyebrow> : null}
        <ValuesHeading className="headline-md" />
        <p className="mt-6 text-foreground/90 text-lg leading-relaxed">{intro}</p>
        {children}
      </>
    );
  }

  return (
    <Section tone={tone}>
      <div className="grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-7">
          {eyebrow ? <SectionEyebrow>{eyebrow}</SectionEyebrow> : null}
          <ValuesHeading />
        </div>
        <div className="lg:col-span-5 self-end">
          <p className="text-foreground/90 text-lg leading-relaxed">{intro}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
        {KCN_VALUES.map((p) => (
          <div key={p.n} className="bg-background p-6 md:p-12">
            <p className="eyebrow-on-dark text-xs uppercase tracking-[0.25em] mb-8">
              {p.n}
            </p>
            <h3 className="font-display text-3xl mb-4">{p.title}</h3>
            <p className="text-foreground/90 leading-relaxed">{p.body}</p>
          </div>
        ))}
      </div>

      {children}
    </Section>
  );
}
